import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import logo4T from './assets/4Thought-Technologies.png';
import ScrollToTop from './components/ScrollToTop';
import { Amplify } from 'aws-amplify';
import { useDarkMode } from './hooks/useDarkMode';
import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import './App.css';

import { HelpTooltipProvider } from './contexts/HelpTooltipContext';

// Pages
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import BookmarkList from './pages/BookmarkList';
import AddBookmark from './pages/AddBookmark';
import TagsPage from './pages/TagsPage';
import CollectionsPage from './pages/CollectionsPage';
import CollectionDetail from './pages/CollectionDetail';
import SharedCollection from './pages/SharedCollection';
import AcceptInvite from './pages/AcceptInvite';
import BrokenLinks from './pages/BrokenLinks';
import Visualization from './pages/Visualization';
import AdminSettings from './pages/AdminSettings';
import AdminFeedback from './pages/AdminFeedback';
import AdminUsers from './pages/AdminUsers';
import AdminReinvites from './pages/AdminReinvites';
import Profile from './pages/Profile';
import Landing from './pages/Landing';
import ImportExport from './pages/ImportExport';
import PublicProfile from './pages/PublicProfile';
import Analytics from './pages/Analytics';
import HelpPage from './pages/HelpPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// AWS Amplify configuration
const amplifyConfig = {
  Auth: {
    Cognito: {
      region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
      userPoolId: process.env.REACT_APP_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID,
      loginWith: {
        email: true,
        preferredUsername: true,
        oauth: {
          // Cognito Managed Login domain — bare hostname, no https://.
          domain: process.env.REACT_APP_COGNITO_AUTH_DOMAIN,
          scopes: ['openid', 'email', 'profile', 'bookmarker-api/access'],
          redirectSignIn: [process.env.REACT_APP_URL],
          redirectSignOut: [process.env.REACT_APP_URL],
          responseType: 'code',
        },
      }
    }
  },
  API: {
    REST: {
      'bookmarker-api': {
        endpoint: process.env.REACT_APP_API_URL,
        region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
      }
    }
  }
};

const mockMode = !process.env.REACT_APP_API_URL || process.env.REACT_APP_MOCK_MODE === 'true';

if (!mockMode && amplifyConfig.Auth.Cognito.userPoolId && amplifyConfig.Auth.Cognito.userPoolClientId) {
  Amplify.configure(amplifyConfig);
}

// Authenticated app shell — routes only reachable when signed in
const AppContent = ({ signOut, isAdmin, userEmail }) => (
  <HelpTooltipProvider>
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
    <Header signOut={signOut} isAdmin={isAdmin} />
    <main className="container mx-auto px-0 sm:px-4 py-8 flex-1">
      <Routes>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/bookmarks" element={<BookmarkList />} />
        <Route path="/add" element={<AddBookmark />} />
        <Route path="/tags" element={<TagsPage />} />
        <Route path="/collections" element={<CollectionsPage />} />
        <Route path="/collections/:collectionId" element={<CollectionDetail />} />
        <Route path="/broken-links" element={<BrokenLinks />} />
        <Route path="/visualize" element={<Visualization />} />
        <Route path="/import-export" element={<ImportExport />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/:articleId" element={<HelpPage />} />
        <Route path="/profile" element={<Profile userEmail={userEmail} />} />
        <Route
          path="/admin"
          element={isAdmin ? <AdminSettings /> : <Navigate to="/dashboard" replace />}
        />
        <Route
          path="/admin/feedback"
          element={isAdmin ? <AdminFeedback /> : <Navigate to="/dashboard" replace />}
        />
        <Route
          path="/admin/users"
          element={isAdmin ? <AdminUsers /> : <Navigate to="/dashboard" replace />}
        />
        <Route
          path="/admin/reinvites"
          element={isAdmin ? <AdminReinvites /> : <Navigate to="/dashboard" replace />}
        />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </main>
    <footer className="border-t border-gray-100 dark:border-gray-700 py-6 mt-8">
      <div className="container mx-auto px-4 flex items-center justify-between text-sm text-gray-400 dark:text-gray-500">
        <div className="flex items-center gap-2">
          <img src={logo4T} alt="4Thought Technologies" className="h-5 opacity-70" />
          <span>© {new Date().getFullYear()} 4Thought Technologies, LLC</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">Terms of Service</Link>
        </div>
      </div>
    </footer>
  </div>
  </HelpTooltipProvider>
);

function App() {
  useDarkMode();
  // In mock mode, start as authenticated so the app shell is immediately available.
  const [authStatus, setAuthStatus] = useState(mockMode ? 'authenticated' : 'loading');
  const [isAdmin, setIsAdmin] = useState(mockMode);
  const [userEmail, setUserEmail] = useState(mockMode ? 'mock@example.com' : '');

  const refreshAuth = useCallback(async (forceRefresh = false) => {
    try {
      const session = await fetchAuthSession({ forceRefresh });
      if (session.tokens) {
        const payload = session.tokens.idToken.payload;
        const groups = payload?.['cognito:groups'];
        setIsAdmin(Array.isArray(groups) && groups.includes('Admins'));
        setUserEmail(payload?.email || '');
        setAuthStatus('authenticated');
      } else {
        setAuthStatus('unauthenticated');
      }
    } catch {
      setAuthStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    if (mockMode) return;

    // If Cognito is redirecting back after PKCE auth, ?code= is in the URL.
    // Calling refreshAuth() now would race the code exchange: fetchAuthSession()
    // returns { tokens: undefined } before Amplify has stored the new tokens,
    // flipping authStatus to 'unauthenticated' and triggering another redirect.
    // Instead, stay at 'loading' and let Hub fire 'signedIn' after the exchange.
    const isOAuthCallback = window.location.search.includes('code=');
    if (!isOAuthCallback) refreshAuth();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshAuth(true);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const stop = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') refreshAuth();
      if (payload.event === 'signedOut') {
        setAuthStatus('unauthenticated');
        setIsAdmin(false);
        setUserEmail('');
      }
      if (payload.event === 'signInWithRedirect_failure') {
        setAuthStatus('unauthenticated');
      }
      // tokenRefresh_failure is intentionally not handled here — see prior comment.
    });

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshAuth]);

  const handleSignOut = async () => {
    // Await local token cleanup so the refresh token is cleared from localStorage
    // before we redirect. If we don't wait, fetchAuthSession() on reload finds
    // the still-valid refresh token and silently re-authenticates the user.
    try {
      await amplifySignOut();
    } catch {
      // If signOut fails, proceed anyway — the Cognito logout redirect below
      // will at least clear the Hosted UI session cookie.
    }

    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_USER_POOL_CLIENT_ID,
      logout_uri: process.env.REACT_APP_URL || window.location.origin,
    });
    window.location.href = `https://${process.env.REACT_APP_COGNITO_AUTH_DOMAIN}/logout?${params}`;
  };

  if (authStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="App">
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Public: landing page — redirect to dashboard if already signed in */}
          <Route
            path="/"
            element={authStatus === 'authenticated'
              ? <Navigate to="/dashboard" replace />
              : <Landing />
            }
          />

          {/* Public: privacy policy and terms — no auth required */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          {/* Public: invitation acceptance — no auth required */}
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />

          {/* Public: shared collection — no auth required */}
          <Route path="/shared/:shareToken" element={<SharedCollection />} />

          {/* Public: user profile — no auth required */}
          <Route path="/u/:profileToken" element={<PublicProfile />} />

          {/* Protected: all app routes — redirect to landing if not signed in */}
          <Route
            path="/*"
            element={authStatus === 'authenticated'
              ? <AppContent signOut={handleSignOut} isAdmin={isAdmin} userEmail={userEmail} />
              : <Navigate to="/" replace />
            }
          />
        </Routes>
      </Router>
    </div>
  );
}

export default App;
